// src/pages/CategoryPage.js
import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import PhotoGrid from "../components/PhotoGrid";
import AdSlot from "../components/AdSlot";
import { usePexels } from "../hooks/usePexels";
import { CATEGORIES } from "./Categories";

// Map slug to search query
const SLUG_TO_QUERY = {
  "nature-wallpaper":       "nature",
  "mountain-wallpaper":     "mountain",
  "beach-wallpaper":        "beach ocean",
  "sunset-wallpaper":       "sunset",
  "forest-wallpaper":       "forest",
  "flower-wallpaper":       "flowers",
  "city-wallpaper":         "city skyline",
  "architecture-wallpaper": "architecture",
  "night-city-wallpaper":   "night city lights",
  "abstract-wallpaper":     "abstract",
  "dark-wallpaper":         "dark aesthetic",
  "minimal-wallpaper":      "minimal",
  "aesthetic-wallpaper":    "aesthetic",
  "galaxy-wallpaper":       "galaxy space",
  "food-photography":       "food",
  "travel-photography":     "travel",
  "fashion-photography":    "fashion",
  "business-photos":        "business",
  "technology-photos":      "technology",
  "people-photography":     "people portrait",
  "iphone-wallpaper":       "mobile wallpaper portrait",
  "android-wallpaper":      "wallpaper portrait",
  "4k-wallpaper":           "landscape 4k",
  "anime-wallpaper":        "anime",
  "gaming-wallpaper":       "gaming",
  "christmas-wallpaper":    "christmas",
};

const CategoryPage = () => {
  const { slug } = useParams();
  const {
    photos, loading, error, retrying, totalResults,
    hasMore, search, loadMore, retry,
  } = usePexels();

  const cat      = CATEGORIES.find(c => c.slug === slug);
  const query    = SLUG_TO_QUERY[slug] || slug.replace(/-/g, " ");
  const title    = cat?.label || slug.replace(/-/g, " ");
  const emoji    = cat?.emoji || "🖼";

  useEffect(() => {
    search(query, "all", ""); // eslint-disable-line
  }, [slug]);

  // Update page title for SEO
  useEffect(() => {
    document.title = `${title} — Free HD Wallpapers & Photos | Halro Images`;
    const metaDesc = document.querySelector("meta[name='description']");
    if (metaDesc) {
      metaDesc.content = `Download free ${title} HD wallpapers and stock photos. 4K quality, no watermark, no signup. ${cat?.desc || ""}`;
    }
  }, [title, cat]);

  if (!cat) {
    return (
      <div className="page page--center">
        <div className="state-box">
          <span className="state-box__icon">🔍</span>
          <h2 className="state-box__title">Category not found</h2>
          <Link to="/categories" className="btn-primary" style={{ marginTop: 16 }}>
            Browse All Categories
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="cat-page-hero" style={{ "--cat-color": cat.color }}>
        <div className="cat-page-hero__bg" />
        <div className="container">
          <Link to="/categories" className="cat-breadcrumb">
            ← All Categories
          </Link>
          <div className="cat-page-hero__content">
            <span className="cat-page-emoji">{emoji}</span>
            <h1 className="cat-page-title">
              Free {title} HD Wallpapers<br />
              <span style={{ color: "var(--gold)" }}>& Stock Photos</span>
            </h1>
            <p className="cat-page-sub">{cat.desc}</p>
            {totalResults > 0 && (
              <span className="results-header__count">
                {totalResults.toLocaleString()}+ photos
              </span>
            )}
          </div>
        </div>
      </div>

      <AdSlot type="banner" />

      <div className="container">
        <PhotoGrid
          photos={photos}
          loading={loading}
          error={error}
          retrying={retrying}
          hasMore={hasMore}
          onLoadMore={loadMore}
          onRetry={retry}
        />

        {/* SEO content for this category */}
        {!loading && photos.length > 0 && (
          <div className="seo-section">
            <h2 className="seo-section__title">
              Free {title} Wallpapers & Photos — Download Now
            </h2>
            <p style={{ color: "var(--text-2)", fontSize: 14, lineHeight: 1.8, maxWidth: 700 }}>
              Looking for free {title.toLowerCase()} wallpapers and stock photos?
              Halro Images offers thousands of high-resolution {title.toLowerCase()} photos
              completely free. Download in original 4K quality, no watermark,
              no attribution required. Perfect for desktop wallpapers, phone backgrounds,
              blog posts, websites and commercial projects.
            </p>

            {/* Related categories */}
            <h3 style={{ fontFamily: "var(--font-display)", fontSize: 18, marginTop: 32, marginBottom: 16 }}>
              Related Categories
            </h3>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {CATEGORIES
                .filter(c => c.slug !== slug)
                .slice(0, 8)
                .map(c => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="chip"
                  >
                    {c.emoji} {c.label}
                  </Link>
                ))}
            </div>
          </div>
        )}

        <AdSlot type="footer" />
      </div>
    </div>
  );
};

export default CategoryPage;
